/**
 * Envoi d'un fichier vers la médiathèque, en trois temps : annonce (avec empreinte SHA-256),
 * envoi direct au stockage par URL signée (avec progression), confirmation puis attente
 * de la vérification côté serveur.
 */
import { api, ApiError, type Asset, type AssetKind, type License, type VideoKind } from "./api";

export const KIND_OF_MIME: Record<string, AssetKind> = {
  "video/mp4": "VIDEO", "image/jpeg": "IMAGE", "image/png": "IMAGE", "image/webp": "IMAGE",
  "audio/ogg": "AUDIO", "audio/mpeg": "AUDIO", "audio/mp4": "AUDIO",
  "model/gltf-binary": "SCENE_3D",
};
/** Les navigateurs ne connaissent pas toujours le type des .glb : on le déduit de l'extension. */
export function normalizeFile(f: File): File {
  if (!f.type && /\.glb$/i.test(f.name)) return new File([f], f.name, { type: "model/gltf-binary", lastModified: f.lastModified });
  return f;
}
export const MAX_BYTES: Record<AssetKind, number> = { VIDEO: 30 * 1024 * 1024, IMAGE: 2 * 1024 * 1024, AUDIO: 8 * 1024 * 1024, SCENE_3D: 5 * 1024 * 1024 };
export const ACCEPT = [...Object.keys(KIND_OF_MIME), ".glb"].join(",");

export async function sha256Hex(file: Blob) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Durée et dimensions lues par le navigateur, sans envoyer le fichier. */
export function readVideoMeta(file: File): Promise<{ durationSec: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    const done = () => URL.revokeObjectURL(url);
    const timer = setTimeout(() => { done(); reject(new Error("Impossible de lire la vidéo (format non pris en charge ?)")); }, 15_000);
    v.onloadedmetadata = () => {
      clearTimeout(timer); done();
      if (!Number.isFinite(v.duration) || !v.videoWidth) reject(new Error("Vidéo illisible"));
      else resolve({ durationSec: Math.max(1, Math.round(v.duration)), width: v.videoWidth, height: v.videoHeight });
    };
    v.onerror = () => { clearTimeout(timer); done(); reject(new Error("Vidéo illisible : utilisez un fichier MP4 (H.264)")); };
    v.src = url;
  });
}

function putWithProgress(url: string, headers: Record<string, string>, file: Blob, onProgress: (f: number) => void, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(xhr.status, "upload_failed", `Envoi refusé par le stockage (${xhr.status})`)));
    xhr.onerror = () => reject(new ApiError(0, "network", "Envoi interrompu : vérifiez la connexion puis relancez."));
    xhr.onabort = () => reject(new ApiError(0, "aborted", "Envoi annulé"));
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(file);
  });
}

export type UploadStage = "hash" | "upload" | "verify";
export type UploadMeta = { title: string; license: License; author: string | null; commercialOk: boolean; videoKind?: VideoKind; languageCode?: string | null };

export async function uploadAsset(file: File, meta: UploadMeta, on: (stage: UploadStage, progress: number) => void, signal?: AbortSignal): Promise<Asset> {
  const kind = KIND_OF_MIME[file.type];
  if (!kind) throw new ApiError(400, "bad_type", "Format non accepté : MP4 pour les vidéos, JPEG/PNG/WebP pour les images, OGG/MP3/M4A pour l'audio, GLB pour les modèles 3D.");
  if (file.size > MAX_BYTES[kind]) throw new ApiError(400, "too_big", `Fichier trop lourd : ${Math.round(MAX_BYTES[kind] / 1024 / 1024)} Mo au plus pour ce type.`);
  on("hash", 0);
  const [sha256, video] = await Promise.all([sha256Hex(file), kind === "VIDEO" ? readVideoMeta(file) : Promise.resolve(undefined)]);
  const { asset, upload } = await api.announceUpload({
    kind, title: meta.title, mimeType: file.type, sizeBytes: file.size, sha256, license: meta.license, author: meta.author, commercialOk: meta.commercialOk,
    languageCode: meta.languageCode ?? null,
    ...(video ? { video: { ...video, videoKind: meta.videoKind ?? "TEACHER_INTRO" } } : {}),
  });
  if (!upload) return asset; // déjà dans la médiathèque
  on("upload", 0);
  await putWithProgress(upload.url, upload.headers, file, (f) => on("upload", f), signal);
  on("verify", 0);
  let a = await api.completeUpload(asset.id);
  // Vérification en tâche de fond : on interroge avec un délai croissant (au plus ~1 min).
  for (let i = 0, wait = 500; a.status === "PENDING" && i < 12; i++, wait = Math.min(wait * 1.6, 8000)) {
    await new Promise((r) => setTimeout(r, wait));
    a = await api.asset(asset.id);
  }
  return a;
}

export const fmtBytes = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1).replace(".", ",")} Mo` : `${Math.max(1, Math.round(n / 1e3))} Ko`);
export const fmtDuration = (s: number) => `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")}`;
